import React, { useEffect, useState } from "react";
import CustomTable from "../../../../components/common/CustomTable";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import { useDispatch } from "react-redux";
import CustomModal from "../../../../components/common/Modal";
import { useFormik } from "formik";
import {
   addContactDetails,
   editContactDetailsAsync,
   fetchSingleContractorMasterAsync,
} from "../../../../redux/features/contractorMasterSlice";
import toast from "react-hot-toast";
import * as Yup from "yup";
import validation from "../../../../helpers/validation";
import { backdropLoadingAction } from "../../../../redux/features/helperSlice";
import { BsPencilSquare } from "react-icons/bs";
import { MAX_DATE } from "../../../../Hooks/dateValidationRules";
import {
   isValidFileType,
   MAX_FILE_SIZE,
   MAX_FILE_SIZE_MESSAGE,
} from "../../../../helpers/fileValidationRules";
import CustomDatePicker from "../../../../components/common/CustomDatePicker/CustomDatePicker";
import CustomFileUpload from "../../../../components/common/CustomFileUpload/CustomFileUpload";
import { useToggleState } from "../../../../Hooks/useToggleState";
import { BASE_URL_IMAGE } from "../../../../Api/client";

const initialValues = {
   id: null,
   startDate: "",
   endDate: "",
   agreementDocument: null,
};

const validationSchema = Yup.object().shape({
   startDate: Yup.date()
      .required("Start Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   endDate: Yup.date()
      .required("End Date is required")
      .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
   agreementDocument: Yup.mixed()
      .required("Agreement document is required")
      .test("is-valid-type", "Not a valid document type", (value) => {
         if (typeof value !== "object") return true;
         return isValidFileType(value && value.name.toLowerCase(), "all");
      })
      .test("is-valid-size", MAX_FILE_SIZE_MESSAGE, (value) => {
         if (typeof value !== "object") return true;
         return value && value.size <= MAX_FILE_SIZE;
      }),
});

const AgreementDetails = ({ data, id }) => {
   const dispatch = useDispatch();

   const [records, setRecords] = useState(data ?? []);
   const [isOpen, setIsOpen] = useState(false);
   //  const [checked, setIsChecked] = useState(false);
   const [action, setAction] = useState("");
   const [isImageModalVisible, handleShowImageModal, handleCloseImageModal] =
      useToggleState();
   const [imageUrl, setImageUrl] = useState(null);

   useEffect(() => {
      if (data) {
         setRecords(data);
      }
   }, [data]);

   const formik = useFormik({
      initialValues,
      validationSchema,
      onSubmit: (formData) => {
         if (action === "add") {
            return handleAddNew(formData);
         } else if (action === "edit") {
            return handleAgreementUpdate(formData);
         }
      },
   });

   const {
      errors,
      touched,
      getFieldProps,
      handleSubmit,
      resetForm,
      setValues,
      setFieldValue,
      values,
   } = formik;

   //Handle Close modal
   const handleClodeModal = () => {
      setIsOpen(false);
      resetForm();
   };

   //Handle Edit Button
   const handleEditButton = (item) => {
      setAction("edit");
      setIsOpen(true);
      setValues({
         id: item.id,
         startDate: item?.start_date,
         endDate: item?.end_date,
         agreementDocument: item?.document_path,
      });
   };

   // Handle Add Button
   const handleAddButton = () => {
      setAction("add");
      setIsOpen(true);
   };

   const columns = [
      {
         name: "Agreement document",
         // selector: (row) => row.document_path,
         selector: (row) => (
            <div
               style={{ textDecoration: "underline", cursor: "pointer" }}
               onClick={() => {
                  setImageUrl(`${BASE_URL_IMAGE}${row.document_path}`);
                  handleShowImageModal();
               }}
            >
               {row.document_path}
            </div>
         ),
      },
      {
         name: "Start date",
         sortable: true,
         selector: (row) => {
            return row.start_date;
         },
      },
      {
         name: "End date",
         sortable: true,
         selector: (row) => row.end_date,
      },
      {
         name: "Action",
         selector: (row) => {
            return (
               <button className="btn mx-2" onClick={() => handleEditButton(row)}>
                  <BsPencilSquare
                     className="text-primary"
                     style={{ height: "1.5rem", width: "1.5rem" }}
                  />
               </button>
            );
         },
         width: "6rem",
      },
   ];

   // Hanlde add new
   const handleAddNew = async (formData) => {
      dispatch(backdropLoadingAction(true));

      const newData = new FormData();
      newData.append("contractor_master_id", id);
      newData.append("start_date", formData.startDate);
      newData.append("end_date", formData.endDate);
      newData.append("agreement_document", formData.agreementDocument);

      newData.append("child_type", "contractor_agreement");

      newData.append("model_id", 9);
      newData.append("action_id", 2);

      dispatch(
         addContactDetails({
            data: newData,
            done: () => {
               toast.success("Updated successfully");
               dispatch(backdropLoadingAction(false));

               handleClodeModal();
               dispatch(
                  fetchSingleContractorMasterAsync({
                     id: id,
                  })
               );
            },
            err: (error) => {
               toast.error(error);
               dispatch(backdropLoadingAction(false));
            },
         })
      );
   };

   // Handle update
   const handleAgreementUpdate = async (formData) => {
      dispatch(backdropLoadingAction(true));
      const updatedata = new FormData();

      updatedata.append("contractor_master_id", id);
      updatedata.append("agreement_id", formData?.id);
      updatedata.append("start_date[]", [formData?.startDate]);
      updatedata.append("end_date[]", [formData?.endDate]);
      if (typeof formData?.agreementDocument === "object") {
         updatedata.append("agreement_document", formData?.agreementDocument);
      }

      updatedata.append("model_id", 9);
      updatedata.append("action_id", 2);
      updatedata.append("_method", "PATCH");

      dispatch(
         editContactDetailsAsync({
            data: updatedata,
            done: () => {
               toast.success("Updated successfully");
               dispatch(backdropLoadingAction(false));
               handleClodeModal();
               dispatch(
                  fetchSingleContractorMasterAsync({
                     id: id,
                  })
               );
            },
            err: (error) => {
               toast.error(error);
               dispatch(backdropLoadingAction(false));
            },
         })
      );
   };

   return (
      <>
         <CustomModal
            showModal={isImageModalVisible}
            handleCloseModal={handleCloseImageModal}
            modalSize="modal-xl"
            title="Agreement document"
            hideFooter
            child={
               <div>
                  {/* <img src={imageUrl} style={{ width: "100%", height: "100%" }} /> */}
                  <object
                     data={imageUrl}
                     style={{ width: "100%", height: "70vh" }}
                  ></object>
               </div>
            }
         />

         <CustomModal
            title={`${action === "edit" ? "Edit" : "Add"} Contact Details`}
            showModal={isOpen}
            handleCloseModal={handleClodeModal}
            onSubmit={handleSubmit}
            modalSize="modal-md"
            child={
               <div>
                  <CustomFileUpload
                     require
                     label="Agreement Document"
                     {...getFieldProps(`agreementDocument`)}
                     onChange={(event, file) => setFieldValue(`agreementDocument`, file)}
                     errors={
                        Boolean(touched.agreementDocument) &&
                        Boolean(errors.agreementDocument)
                     }
                     message={errors.agreementDocument}
                  />
                  <CustomDatePicker
                     require
                     label="Start date"
                     placeholder="Start date"
                     {...getFieldProps(`startDate`)}
                     errors={Boolean(touched.startDate) && Boolean(errors.startDate)}
                     message={errors.startDate}
                  />
                  <CustomDatePicker
                     require
                     label="End date"
                     placeholder="End date"
                     {...getFieldProps(`endDate`)}
                     errors={Boolean(touched.endDate) && Boolean(errors.endDate)}
                     message={errors.endDate}
                  />
               </div>
            }
         />

         <div className="row justify-content-end">
            <div className="d-flex justify-content-end pb-1">
               <button className="btn btn-primary" onClick={handleAddButton}>
                  Add Agreement
               </button>
            </div>
            <CustomTable columns={columns} data={records} />
         </div>
      </>
   );
};

export default AgreementDetails;
